import React from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams, Navigate } from "react-router-dom";
import {
  CheckCircle2,
  MessageCircle,
  ChevronLeft,
  Sparkles,
} from "lucide-react";

import { Nav } from "../components/landing/Nav";
import { Footer } from "../components/landing/Footer";
import { WhatsAppFloat } from "../components/landing/WhatsAppFloat";
import { whatsappLink } from "../lib/site";
import { getSpecialty } from "../data/specialtiesContent";

// Render HTML safely inside paragraphs (we only allow <strong>)
const RichText = ({ html, className }) => (
  <p className={className} dangerouslySetInnerHTML={{ __html: html }} />
);

export default function SpecialtyPage() {
  const { slug } = useParams();
  const data = getSpecialty(slug);

  if (!data) {
    return <Navigate to="/" replace />;
  }

  const { hero, treatment, finalCta, breadcrumb } = data;

  return (
    <div data-testid={`specialty-page-${data.slug}`} className="bg-white text-[#1A1A1A]">
    <Helmet>
      <title>{data.seo.title}</title>
      <link
      rel="icon"
      type="image/x-icon"
      href="https://draluzadriana.com.br/dra-luz-adriana-google.png"
      />

      <link
      rel="shortcut icon"
      type="image/x-icon"
      href="https://draluzadriana.com.br/dra-luz-adriana-google.png"
      />
      
      <meta
        name="description"
        content={data.seo.description}
      />

      <link
        rel="canonical"
        href={`https://draluzadriana.com.br/especialidades/${data.slug}`}
      />

      <meta property="og:type" content="website" />

      <meta
        property="og:title"
        content={data.seo.title}
      />

      <meta
        property="og:description"
        content={data.seo.description}
      />

      <meta
        property="og:url"
        content={`https://draluzadriana.com.br/especialidades/${data.slug}`}
      />

      <meta
        property="og:site_name"
        content="Dra. Luz Adriana"
      />

      <meta
        name="robots"
        content="index, follow"
      />
    </Helmet>

      <Nav />

      {/* Breadcrumb */}
      <div className="bg-[#F8F9FA] border-b border-[#E9ECEF]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-3 text-xs text-[#0A2540]/70 flex items-center gap-2">
          <Link
            to="/"
            className="hover:text-[#D4AF37] inline-flex items-center gap-1"
            data-testid="breadcrumb-home"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            Início
          </Link>
          <span>/</span>
          <span>Especialidades</span>
          <span>/</span>
          <span className="text-[#0A2540] font-semibold">{breadcrumb}</span>
        </div>
      </div>

      {/* HERO */}
      {hero.variant === "informative" ? (
        <section data-testid="specialty-hero" className="hero-bg relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 pt-14 lg:pt-24 pb-20 lg:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* LEFT */}
            <div className="lg:col-span-7 reveal">
              <p className="overline text-[#D4AF37] mb-6">{hero.overline}</p>
              <h1
                data-testid="specialty-headline"
                className="font-serif-display text-5xl sm:text-6xl lg:text-7xl text-[#0A2540] leading-[1.05] tracking-tight"
              >
                {hero.title}
                <span className="text-[#D4AF37]">.</span>
              </h1>

              <RichText
                html={hero.intro}
                className="mt-8 text-lg text-[#1A1A1A]/75 leading-relaxed max-w-2xl"
              />
              {hero.complementary && (
                <p className="mt-5 text-base text-[#1A1A1A]/65 leading-relaxed max-w-2xl">
                  {hero.complementary}
                </p>
              )}

              {/* Symptoms box */}
              {hero.symptomsList && (
                <div
                  data-testid="specialty-hero-symptoms"
                  className="mt-10 bg-white border border-[#E9ECEF] rounded-lg p-6 sm:p-7 max-w-2xl shadow-sm"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <Sparkles className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.6} />
                    <p className="overline text-[#0A2540]">
                      {hero.symptomsTitle || "Principais sintomas"}
                    </p>
                  </div>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                    {hero.symptomsList.map((s) => (
                      <li
                        key={s}
                        className="flex items-start gap-2.5 text-sm text-[#1A1A1A]/80 leading-snug"
                      >
                        <span
                          aria-hidden
                          className="mt-2 w-1.5 h-1.5 rounded-full bg-[#D4AF37] flex-shrink-0"
                        />
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <a
                  data-testid="specialty-cta-hero"
                  href={whatsappLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A2540] font-semibold px-8 py-4 rounded-sm transition-all duration-300 shadow-md hover:-translate-y-0.5"
                >
                  <MessageCircle className="w-5 h-5" />
                  Falar com Especialista
                </a>
              </div>

              <ul
                data-testid="specialty-indicators"
                className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 max-w-2xl"
              >
                {hero.indicators.map((ind) => (
                  <li key={ind} className="flex items-start gap-3 text-[#0A2540]/85 text-sm">
                    <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span>{ind}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* RIGHT — Image */}
            <div className="lg:col-span-5 reveal">
              <div className="relative">
                <div
                  aria-hidden
                  className="absolute -inset-6 rounded-2xl bg-gradient-to-br from-[#D4AF37]/15 via-transparent to-[#0D3B66]/15 blur-2xl"
                />
                <div className="relative aspect-[4/5] w-full max-w-md mx-auto">
                  <img
                    data-testid="specialty-hero-image"
                    src={hero.image}
                    alt={hero.imageCaption || hero.title}
                    className="w-full h-full object-cover rounded-2xl shadow-2xl"
                  />
                  <div
                    aria-hidden
                    className="absolute inset-0 rounded-2xl ring-1 ring-white/10"
                  />
                  {hero.imageCaption && (
                    <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 bg-white shadow-xl border border-[#E9ECEF] rounded-full px-5 py-2.5 whitespace-nowrap max-w-[92%]">
                      <p className="text-xs text-[#0A2540]/75 truncate">
                        {hero.imageCaption}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : (
      <section data-testid="specialty-hero" className="hero-bg relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 pt-14 lg:pt-24 pb-20 lg:pb-28">
          <div className="max-w-4xl reveal">
            <p className="overline text-[#D4AF37] mb-6">{hero.overline}</p>
            <h1
              data-testid="specialty-headline"
              className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#0A2540] leading-[1.05] tracking-tight"
            >
              {hero.titlePre}{" "}
              <span className="italic font-normal text-[#0D3B66]">
                {hero.titleItalic}
              </span>
              <span className="text-[#D4AF37]">.</span>
            </h1>

            {hero.paragraphs.map((p, i) => (
              <RichText
                key={i}
                html={p}
                className={
                  i === 0
                    ? "mt-8 text-lg sm:text-xl text-[#1A1A1A]/75 leading-relaxed max-w-3xl"
                    : i === hero.paragraphs.length - 1
                    ? "mt-5 text-base text-[#1A1A1A]/65 leading-relaxed max-w-3xl"
                    : "mt-5 text-base sm:text-lg text-[#1A1A1A]/70 leading-relaxed max-w-3xl"
                }
              />
            ))}

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a
                data-testid="specialty-cta-hero"
                href={whatsappLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A2540] font-semibold px-8 py-4 rounded-sm transition-all duration-300 shadow-md hover:-translate-y-0.5"
              >
                <MessageCircle className="w-5 h-5" />
                Falar com Especialista
              </a>
            </div>

            <ul
              data-testid="specialty-indicators"
              className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 max-w-2xl"
            >
              {hero.indicators.map((ind) => (
                <li key={ind} className="flex items-start gap-3 text-[#0A2540]/85 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <span>{ind}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
      )}

      {/* TREATMENT */}
      <section
        data-testid="specialty-treatment"
        className="py-20 sm:py-24 lg:py-32 bg-[#F8F9FA]"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="overline text-[#D4AF37] mb-5">{treatment.overline}</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A2540] leading-tight tracking-tight">
              {treatment.titlePre}{" "}
              <span className="italic font-normal text-[#0D3B66]">
                {treatment.titleItalic}
              </span>
              .
            </h2>
            <p className="mt-6 text-[#1A1A1A]/70 leading-relaxed">{treatment.intro}</p>

            <div className="mt-8 bg-white border border-[#E9ECEF] rounded-lg p-6">
              <Sparkles className="w-6 h-6 text-[#D4AF37] mb-3" strokeWidth={1.6} />
              <RichText
                html={treatment.sparkleBox}
                className="text-sm text-[#1A1A1A]/75 leading-relaxed"
              />
            </div>
          </div>

          <div className="lg:col-span-7">
            <ul className="space-y-4" data-testid="specialty-benefits-list">
              {treatment.benefits.map((b, i) => (
                <li
                  key={b}
                  data-testid={`specialty-benefit-${i}`}
                  className="card-lift bg-white border-l-4 border-[#D4AF37] rounded-r-lg px-6 py-5 flex items-start gap-4"
                >
                  <CheckCircle2 className="w-6 h-6 text-[#0D3B66] flex-shrink-0 mt-0.5" />
                  <span className="text-[#0A2540] font-medium leading-snug">{b}</span>
                </li>
              ))}
            </ul>

            <RichText
              html={treatment.outro}
              className="mt-8 text-[#1A1A1A]/70 leading-relaxed"
            />
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section
        data-testid="specialty-final-cta"
        className="relative py-24 sm:py-28 lg:py-36 bg-[#0A2540] text-white overflow-hidden"
      >
        <div
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, #D4AF37 0%, transparent 35%), radial-gradient(circle at 80% 80%, #0D3B66 0%, transparent 45%)",
          }}
          aria-hidden
        />
        <div className="relative max-w-4xl mx-auto px-6 sm:px-8 text-center">
          <p className="overline text-[#D4AF37] mb-6">{finalCta.overline}</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            Sua qualidade de vida merece{" "}
            <span className="italic font-normal text-[#D4AF37]">
              atenção especializada
            </span>
            .
          </h2>

          <div className="mt-10 space-y-5 text-white/80 leading-relaxed text-lg max-w-3xl mx-auto text-left sm:text-center">
            {finalCta.paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          <p className="mt-10 font-serif-display text-2xl sm:text-3xl text-white/95 max-w-2xl mx-auto leading-snug">
            {finalCta.closing}
          </p>

          <a
            data-testid="specialty-final-whatsapp"
            href={whatsappLink()}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-12 inline-flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#20BD5A] text-white text-lg font-semibold px-10 py-5 rounded-sm shadow-2xl transition-transform hover:-translate-y-1"
          >
            <MessageCircle className="w-6 h-6" />
            Falar com Especialista
          </a>

          <p className="mt-10 text-xs text-white/45">
            Atendimento humanizado · Avaliação individualizada · Resposta rápida via WhatsApp
          </p>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
